import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { TemplatesProvider } from './context/TemplatesContext';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ToastContainer from './components/common/ToastContainer';
import PublicLayout from './components/public/PublicLayout';

// ===== Public frontend pages =====
const HomePage = lazy(() => import('./pages/public/HomePage'));
const ProductCatalog = lazy(() => import('./pages/public/ProductCatalog'));
const ProductDetail = lazy(() => import('./pages/public/ProductDetail'));
const CaseGallery = lazy(() => import('./pages/public/CaseGallery'));
const CaseDetail = lazy(() => import('./pages/public/CaseDetail'));
const PricingPage = lazy(() => import('./pages/public/PricingPage'));

// Lazy load admin page components for code splitting
const Login = lazy(() => import('./pages/Login'));
const QuotationForm = lazy(() => import('./pages/QuotationForm'));
const AirconQuotationForm = lazy(() => import('./pages/AirconQuotationForm'));
const AirconQuotationDetail = lazy(() => import('./pages/AirconQuotationDetail'));
const PublicQuotationView = lazy(() => import('./pages/PublicQuotationView'));
const QuotationList = lazy(() => import('./pages/QuotationList'));
const QuotationDetail = lazy(() => import('./pages/QuotationDetail'));
const ShippingDocumentPage = lazy(() => import('./pages/ShippingDocumentPage'));
const InvoiceList = lazy(() => import('./pages/InvoiceList'));
const InvoiceDetail = lazy(() => import('./pages/InvoiceDetail'));
const MessageTemplatesSettings = lazy(() => import('./pages/MessageTemplatesSettings'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const CompanyManagement = lazy(() => import('./pages/CompanyManagement'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Customer management pages
const CustomerList = lazy(() => import('./pages/CustomerList'));
const CustomerDetail = lazy(() => import('./pages/CustomerDetail'));
const CustomerForm = lazy(() => import('./pages/CustomerForm'));

// Asset management pages
const AssetList = lazy(() => import('./pages/AssetList'));
const AssetForm = lazy(() => import('./pages/AssetForm'));

// Schedule Calendar page
const ScheduleCalendar = lazy(() => import('./pages/ScheduleCalendar'));

// Dashboard Simple page
const DashboardSimple = lazy(() => import('./pages/DashboardSimple'));

// Signature page (public access)
const SignaturePage = lazy(() => import('./pages/SignaturePage'));

// Estimation wizard (public, no auth required)
const EstimationWizard = lazy(() => import('./pages/EstimationWizard'));

// Estimation management pages (admin)
const EstimationList = lazy(() => import('./pages/EstimationList'));
const EstimationDetail = lazy(() => import('./pages/EstimationDetail'));

// Admin CMS pages
const ProductManagement = lazy(() => import('./pages/ProductManagement'));
const CaseStudyManagement = lazy(() => import('./pages/CaseStudyManagement'));
const SecuritySettings = lazy(() => import('./pages/SecuritySettings'));

// Loading component
const LoadingFallback = () => (
  <div className="loading-container">
    <div className="loading-spinner"></div>
    <p>載入中...</p>
  </div>
);

// Admin layout wraps the original backend with Header/Footer
const AdminLayout = ({ children }) => {
  const location = useLocation();
  const isSignaturePage = location.pathname.startsWith('/signature/');

  return (
    <div className="App">
      {!isSignaturePage && <Header />}
      <main className={isSignaturePage ? "signature-container" : "container"}>
        {children}
      </main>
      {!isSignaturePage && <Footer />}
      <ToastContainer />
    </div>
  );
};

// All admin sub-routes rendered inside AdminLayout
const AdminSection = () => (
  <AdminLayout>
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Admin dashboard (root of /admin) */}
        <Route element={<ProtectedRoute />}>
          <Route index element={<DashboardSimple />} />
          <Route path="profile" element={<UserProfile />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="quotations" element={<QuotationList />} />
          <Route path="quotations/:id" element={<QuotationDetail />} />
          <Route path="aircon-quotations/:id" element={<AirconQuotationDetail />} />
          <Route path="shipping/:id" element={<ShippingDocumentPage />} />
          <Route path="create" element={<QuotationForm />} />
          <Route path="create-aircon" element={<AirconQuotationForm />} />
          {/* AIRQU-32：空調報價單編輯路由，沿用 AirconQuotationForm（edit 模式） */}
          <Route path="edit-aircon/:id" element={<AirconQuotationForm />} />
          <Route path="edit/:id" element={<QuotationForm />} />
          <Route path="invoices" element={<InvoiceList />} />
          <Route path="invoices/:id" element={<InvoiceDetail />} />
          <Route path="customers" element={<CustomerList />} />
          <Route path="customers/new" element={<CustomerForm />} />
          <Route path="customers/:id" element={<CustomerDetail />} />
          <Route path="customers/:id/edit" element={<CustomerForm />} />
          <Route path="assets" element={<AssetList />} />
          <Route path="assets/new" element={<AssetForm />} />
          <Route path="assets/:id/edit" element={<AssetForm />} />
          <Route path="dashboard-simple" element={<DashboardSimple />} />
          <Route path="schedule" element={<ScheduleCalendar />} />
        </Route>

        {/* Estimation management Routes (admin + superadmin) */}
        <Route element={<ProtectedRoute allowedRoles={['admin', 'superadmin']} />}>
          <Route path="estimations" element={<EstimationList />} />
          <Route path="estimations/:id" element={<EstimationDetail />} />
        </Route>

        {/* Admin-only Routes */}
        <Route element={<ProtectedRoute allowedRoles={['admin', 'superadmin']} />}>
          <Route path="message-templates" element={<MessageTemplatesSettings />} />
        </Route>

        {/* Superadmin-only Routes */}
        <Route element={<ProtectedRoute allowedRoles={['superadmin']} />}>
          <Route path="companies" element={<CompanyManagement />} />
          <Route path="users" element={<UserManagement />} />
        </Route>

        {/* Admin + Superadmin */}
        <Route element={<ProtectedRoute allowedRoles={['admin', 'superadmin']} />}>
          <Route path="companies/:companyId/users" element={<UserManagement />} />
          <Route path="products" element={<ProductManagement />} />
          <Route path="case-studies" element={<CaseStudyManagement />} />
          <Route path="security" element={<SecuritySettings />} />
        </Route>
      </Routes>
    </Suspense>
  </AdminLayout>
);

function App() {
  return (
    <Router>
      <AuthProvider>
        <TemplatesProvider>
          <ToastProvider>
            <Routes>
              {/* ===== PUBLIC FRONTEND ROUTES ===== */}
              <Route path="/" element={
                <PublicLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <HomePage />
                  </Suspense>
                </PublicLayout>
              } />
              <Route path="/products" element={
                <PublicLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <ProductCatalog />
                  </Suspense>
                </PublicLayout>
              } />
              <Route path="/products/:id" element={
                <PublicLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <ProductDetail />
                  </Suspense>
                </PublicLayout>
              } />
              <Route path="/cases" element={
                <PublicLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <CaseGallery />
                  </Suspense>
                </PublicLayout>
              } />
              <Route path="/cases/:id" element={
                <PublicLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <CaseDetail />
                  </Suspense>
                </PublicLayout>
              } />
              <Route path="/pricing" element={
                <PublicLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <PricingPage />
                  </Suspense>
                </PublicLayout>
              } />

              {/* ===== ESTIMATION WIZARD (PUBLIC) ===== */}
              <Route path="/estimation" element={
                <div className="App">
                  <main className="signature-container">
                    <Suspense fallback={<LoadingFallback />}>
                      <EstimationWizard />
                    </Suspense>
                  </main>
                  <ToastContainer />
                </div>
              } />

              {/* ===== PUBLIC QUOTATION SHARE LINK ===== */}
              <Route path="/q/:token" element={
                <div className="App">
                  <main className="signature-container">
                    <Suspense fallback={<LoadingFallback />}>
                      <PublicQuotationView />
                    </Suspense>
                  </main>
                  <ToastContainer />
                </div>
              } />

              {/* ===== SIGNATURE (PUBLIC) ===== */}
              <Route path="/signature/:token" element={
                <div className="App">
                  <main className="signature-container">
                    <Suspense fallback={<LoadingFallback />}>
                      <SignaturePage />
                    </Suspense>
                  </main>
                  <ToastContainer />
                </div>
              } />

              {/* ===== LOGIN ===== */}
              <Route path="/login" element={
                <div className="App">
                  <main className="container">
                    <Suspense fallback={<LoadingFallback />}>
                      <Login />
                    </Suspense>
                  </main>
                  <ToastContainer />
                </div>
              } />

              {/* ===== ADMIN SECTION ===== */}
              <Route path="/admin/*" element={<AdminSection />} />

              {/* ===== 404 ===== */}
              <Route path="*" element={
                <PublicLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <NotFound />
                  </Suspense>
                </PublicLayout>
              } />
            </Routes>
          </ToastProvider>
        </TemplatesProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
